import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ExternalRecipient } from './types';
import {
  ExternalRecipientResponse,
  CreateExternalRecipientPayload,
  UpdateExternalRecipientPayload,
  fetchExternalRecipients,
  createExternalRecipient,
  updateExternalRecipient,
  deleteExternalRecipient,
} from './externalRecipientsApi';

interface ExternalRecipientsState {
  items: ExternalRecipient[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pages: number;
  };
  search: string;
}

const initialState: ExternalRecipientsState = {
  items: [],
  loading: false,
  error: null,
  pagination: { page: 1, pageSize: 50, total: 0, pages: 0 },
  search: '',
};

export const loadExternalRecipients = createAsyncThunk<
  ExternalRecipientResponse,
  { search?: string; page?: number; pageSize?: number; includeInactive?: boolean } | undefined
>('externalRecipients/load', async (params) => {
  return fetchExternalRecipients(params);
});

export const addExternalRecipient = createAsyncThunk<ExternalRecipient, CreateExternalRecipientPayload>(
  'externalRecipients/add',
  async (payload) => createExternalRecipient(payload)
);

export const editExternalRecipient = createAsyncThunk<
  ExternalRecipient,
  { id: string; payload: UpdateExternalRecipientPayload }
>('externalRecipients/edit', async ({ id, payload }) => updateExternalRecipient(id, payload));

export const removeExternalRecipient = createAsyncThunk<string, string, { rejectValue: string }>(
  'externalRecipients/remove',
  async (id, { rejectWithValue }) => {
    try {
      await deleteExternalRecipient(id);
      return id;
    } catch (error: any) {
      const msg =
        typeof error?.message === 'string' ? error.message : 'No se pudo eliminar el destinatario';
      return rejectWithValue(msg);
    }
  }
);

const externalRecipientsSlice = createSlice({
  name: 'externalRecipients',
  initialState,
  reducers: {
    setExternalRecipientsSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadExternalRecipients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadExternalRecipients.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadExternalRecipients.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudieron cargar los destinatarios';
      })
      .addCase(addExternalRecipient.fulfilled, (state, action) => {
        state.items = [...state.items, action.payload];
      })
      .addCase(editExternalRecipient.fulfilled, (state, action) => {
        state.items = state.items.map((r) =>
          r.id === action.payload.id ? action.payload : r
        );
      })
      .addCase(removeExternalRecipient.fulfilled, (state, action) => {
        state.items = state.items.filter((r) => r.id !== action.payload);
      })
      .addCase(removeExternalRecipient.rejected, (state, action) => {
        if (action.payload) {
          state.error = action.payload;
        }
      });
  },
});

export const { setExternalRecipientsSearch } = externalRecipientsSlice.actions;

export default externalRecipientsSlice.reducer;